import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { sanitizeHtml } from '../../../../assets/utils/comm/sanitize';
import Http from '../../../../core/Http';

/**
 * 마이페이지 회원 탈퇴 전용 공통 팝업.
 * config.content — 안내 HTML(선택), config.initialData.userUid — 탈퇴 대상 UID
 * 확인 시 onClose(true, { withdrawn: true, deleteMode, permanent })
 */
const AccountWithdrawPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const userUid = config?.initialData?.userUid;
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPassword('');
    setBusy(false);
    setError('');
  }, []);

  const handleCancel = () => onClose(false);

  const apiMessage = (err) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    t('popup.accountWithdraw.errorRequestFailed');

  const handleWithdraw = async () => {
    if (userUid == null) {
      setError(t('popup.accountWithdraw.errorNoUser'));
      return;
    }
    const pw = (password || '').trim();
    if (!pw) {
      setError(t('popup.accountWithdraw.errorNoPassword'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await Http.put(`/api/portal/user/withdraw/${userUid}`, {
        password: pw,
        deleteMode: 'soft',
      });
      onClose(true, {
        withdrawn: true,
        deleteMode: data?.deleteMode ?? 'soft',
        permanent: Boolean(data?.permanent),
      });
    } catch (err) {
      setError(typeof apiMessage(err) === 'string' ? apiMessage(err) : t('popup.accountWithdraw.errorWithdrawFailed'));
    } finally {
      setBusy(false);
    }
  };

  const defaultNotice = t('popup.accountWithdraw.defaultNotice');

  return (
    <PopupWrapper
      config={{
        ...config,
        title: config?.title || t('popup.accountWithdraw.title'),
        width: config?.width ?? 440,
        leftButton: null,
        rightButton: null,
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-account-withdraw"
    >
      <div className="main-popup-body popup-account-withdraw-body">
        <div
          className="mb-3"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(config?.content || defaultNotice),
          }}
        />
        <label className="form-label small mb-1" htmlFor="account-withdraw-password">
          {t('popup.accountWithdraw.passwordLabel')}
        </label>
        <input
          id="account-withdraw-password"
          type="password"
          className="form-control form-control-sm"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('popup.accountWithdraw.passwordPlaceholder')}
          disabled={busy}
        />
        {error ? <p className="small text-danger mt-2 mb-0">{error}</p> : null}
      </div>
      <div className="main-popup-footer d-flex justify-content-between gap-2">
        <button
          type="button"
          className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`}
          onClick={handleCancel}
          disabled={busy}
        >
          {t('popup.common.cancel')}
        </button>
        <button
          type="button"
          className={`btn main-popup-right-btn ${getButtonClass('btn-danger')}`}
          onClick={handleWithdraw}
          disabled={busy}
        >
          {busy ? t('popup.common.processingBusy') : t('popup.accountWithdraw.withdrawButton')}
        </button>
      </div>
    </PopupWrapper>
  );
};

export default AccountWithdrawPopup;
